import { useDeferredValue, useMemo } from 'react';

import { useMy } from '@/hooks/useMy';
import { useGlobalState } from '@/state/global.state';
import {
  RISK_METRICS_CACHE,
  STALE_WHILE_REVALIDATE_CACHE,
} from '@/utils/staleWhileRevalidateCache.util';

/**
 * Shared hook for widgets that fetch statistic data based on global period.
 *
 * Encapsulates common pattern across 7+ dashboard widgets:
 * - Accesses global dashboard period state
 * - Fetches data with stale-while-revalidate caching
 * - Processes data with memoization
 * - Defers data for smooth chart rendering
 * - Provides loading state
 *
 * This hook reduces ~20 lines of boilerplate per widget and ensures
 * consistent data fetching patterns across the dashboard.
 *
 * @template T - Raw API data type (from useMy)
 * @template P - Processed data type (for component consumption)
 *
 * @param resource - API resource type (e.g., 'statistic')
 * @param queryBuilder - Function that builds query string from period (optional if query doesn't use period)
 * @param processor - Function that transforms raw data to component data
 * @param options - Optional configuration
 * @param options.useStaticQuery - If true, uses static query instead of period-based query builder
 * @param options.staticQuery - Static query string (required if useStaticQuery is true)
 * @param options.to - Optional 'to' parameter for useMy query range
 *
 * @returns Widget data state and loading indicators
 *
 * @example
 * // Period-based query (most common pattern)
 * ```tsx
 * const { data, isLoading } = useWidgetData(
 *   'statistic',
 *   (daysAgo) => `#event##risk##${getDaysAgo(daysAgo)}`,
 *   (raw, period) => raw ? processVulnerabilities(raw, period) : getDefaultData(period)
 * );
 *
 * if (isLoading) {
 *   return <Loader type="spinner" isLoading={true} className="h-full" />;
 * }
 *
 * return <Chart data={data} />;
 * ```
 *
 * @example
 * // Static query (doesn't use period)
 * ```tsx
 * const { data, isLoading } = useWidgetData(
 *   'statistic',
 *   null,
 *   (raw) => raw ? processData(raw) : getDefaultData(),
 *   { useStaticQuery: true, staticQuery: '#asset#country#now' }
 * );
 * ```
 *
 * @example
 * // With query range (to parameter)
 * ```tsx
 * const { data, isLoading } = useWidgetData(
 *   'statistic',
 *   (daysAgo) => `#event##risk##${getDaysAgo(daysAgo)}`,
 *   (raw, period) => processData(raw, period),
 *   { to: '#statistic#event##risk##now&' }
 * );
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useWidgetData<T = any, P = T>(
  resource: string,
  queryBuilder: ((daysAgo: number) => string) | null,
  processor: (data: T | undefined, period: number) => P,
  options?: {
    useStaticQuery?: boolean;
    staticQuery?: string;
    to?: string;
    cacheConfig?:
      | typeof STALE_WHILE_REVALIDATE_CACHE
      | typeof RISK_METRICS_CACHE;
  }
) {
  const {
    dashboard: { period },
  } = useGlobalState();

  // Build query based on configuration
  const query = options?.useStaticQuery
    ? options.staticQuery!
    : queryBuilder
      ? queryBuilder(Number(period))
      : '';

  const { data, status, isPlaceholderData } = useMy(
    {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      resource: resource as any,
      query,
      ...(options?.to && { to: options.to }),
    },
    options?.cacheConfig ?? STALE_WHILE_REVALIDATE_CACHE
  );

  const processed = useMemo(() => {
    return processor(data as T | undefined, Number(period));
  }, [data, period, processor]);

  // Defer data for smooth chart rendering during period changes
  const deferredData = useDeferredValue(processed);

  return {
    data: deferredData,
    rawData: data,
    status,
    isPlaceholderData,
    isLoading: status === 'pending' && !isPlaceholderData,
    period: Number(period),
  };
}
